import React, { Suspense, lazy, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { RecaptchaVerifier } from "firebase/auth";
import { auth } from "./utils/firebase";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";
import { TermsProvider } from "./contexts/TermsContext";

// Instance globale reCAPTCHA
declare global {
  interface Window {
    __heRecaptchaVerifier?: RecaptchaVerifier | null;
    __heRecaptchaWidgetId?: number | null;
    __heRecaptchaInitInProgress?: boolean;
  }
}
import { KorisProvider } from "./contexts/KorisContext";
import OptimizedHeader from "./components/layout/OptimizedHeader";
import Footer from "./components/layout/Footer";
import LoadingSpinner from "./components/ui/LoadingSpinner";
import ErrorBoundary from "./components/ui/ErrorBoundary";
import ScrollToTop from "./components/utils/ScrollToTop";
import FloatingChat from "./components/DrLoChat/FloatingChat";
import CompatInviteResume from "./components/CompatInviteResume";
import UpdateBanner from "./components/ui/UpdateBanner";
import WelcomeFlow from "./components/Onboarding/WelcomeFlow";
import HelpButton from "./components/Onboarding/HelpButton";
import NoKorisModal from "./components/koris/NoKorisModal";
import KorisWelcome from "./components/koris/KorisWelcome";
import FloatingKori from "./components/koris/FloatingKori";
import BottomTabs from "./components/layout/BottomTabs";
import { noteLocation } from "./utils/navTrail";

import { useAuth } from "./contexts/AuthContext";
import { cleanupAllProfessionalsListeners } from "./hooks/useProfessionals";
import { cleanupAllBookingListeners } from "./hooks/useBookings";
import {
  cleanupAllMessageListeners,
  clearMessageCaches,
} from "./services/messageService";
import {
  ensureFirestoreReady,
  resetFirestoreConnection,
  ensureRequiredCollectionsExist,
} from "./utils/firebase";

// Lazy load pages for better performance
const HomePage = lazy(() => import("./pages/AccueilPage"));
const CompagnonPage = lazy(() => import("./pages/CompagnonPage"));
const EspacePage = lazy(() => import("./pages/drlo/EspacePage"));
const DomainePage = lazy(() => import("./pages/drlo/DomainePage"));
const HumeurPage = lazy(() => import("./pages/drlo/HumeurPage"));
const QuestionPage = lazy(() => import("./pages/drlo/QuestionPage"));
const DrLoConsole = lazy(() => import("./pages/drlo/DrLoConsole"));
const EquipePage = lazy(() => import("./pages/drlo/EquipePage"));
const MessagesDrLoPage = lazy(() => import("./pages/drlo/MessagesDrLoPage"));
const ChainesPage = lazy(() => import("./pages/drlo/ChainesPage"));
const ChannelInvitePage = lazy(() => import("./pages/drlo/ChannelInvitePage"));
const PratiquePage = lazy(() => import("./pages/drlo/PratiquePage"));
const RoutinePage = lazy(() => import("./pages/drlo/RoutinePage"));
const BoiteOutilsPage = lazy(() => import("./pages/drlo/BoiteOutilsPage"));
const PencPage = lazy(() => import("./pages/drlo/PencPage"));
const ProApplyPage = lazy(() => import("./pages/drlo/ProApplyPage"));
const InfosPage = lazy(() => import("./pages/drlo/InfosPage"));
const PointPage = lazy(() => import("./pages/drlo/PointPage"));
const BoitePage = lazy(() => import("./pages/drlo/BoitePage"));
const OutilsPage = lazy(() => import("./pages/drlo/OutilsPage"));
const MonJournalPage = lazy(() => import("./pages/drlo/MonJournalPage"));
const ResultatPage = lazy(() => import("./pages/drlo/ResultatPage"));
const PatientAccess = lazy(() => import("./pages/patient/PatientAccess"));
const ForgotPassword = lazy(() => import("./components/auth/ForgotPassword"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));

const ProtectedRoute = lazy(() => import("./components/auth/ProtectedRoute"));
const CompanionApp = lazy(() => import("./companion/CompanionApp"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminContent = lazy(() => import("./pages/admin/AdminContent"));
const AdminNotifications = lazy(
  () => import("./pages/admin/AdminNotificationsPage")
);
const AdminEvaluations = lazy(
  () => import("./pages/admin/AdminEvaluations")
);
const AdminKorisCredit = lazy(
  () => import("./pages/admin/AdminKorisCredit")
);
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Ethics = lazy(() => import("./pages/Ethics"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));

// Journal pages
const AcheterKorisPage = lazy(() => import("./pages/AcheterKorisPage"));

// Assessment pages
const CategoryGate = lazy(() => import("./pages/assessment/CategoryGate"));
const AssessmentQuizPage = lazy(() => import("./pages/assessment/AssessmentQuizPage"));
const AssessmentResultsPage = lazy(() => import("./pages/assessment/AssessmentResultsPage"));
const CompatibilityPage = lazy(() => import("./pages/assessment/CompatibilityPage"));
const TestDetailPage = lazy(() => import("./pages/assessment/TestDetailPage"));
const TestCodeRedirect = lazy(() => import("./pages/assessment/TestDetailPage").then(m => ({ default: m.TestCodeRedirect })));
const AssessmentProfilePage = lazy(() => import("./pages/assessment/AssessmentProfilePage"));
const TotemProfilePage = lazy(() => import("./pages/assessment/TotemProfilePage"));
const DefiPage = lazy(() => import("./pages/DefiPage"));
const DefiCreerPage = lazy(() => import("./pages/DefiCreerPage"));
const DefiPartagePage = lazy(() => import("./pages/DefiPartagePage"));
const DefiJouerPage = lazy(() => import("./pages/DefiJouerPage"));
const DefiSuiviPage = lazy(() => import("./pages/DefiSuiviPage"));
const DefiMesDefisPage = lazy(() => import("./pages/DefiMesDefisPage"));
const DecouvreDefiPage = lazy(() => import("./pages/DecouvreDefiPage"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: "#17120E" }}>
    <div className="text-center">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-lg" style={{ color: "#CFC6B8" }}>Chargement…</p>
    </div>
  </div>
);

// Wrapper component that decides whether to show header/footer based on route
const AppChrome: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => { noteLocation(pathname); }, [pathname]);
  const isAdminRoute = pathname.startsWith("/admin");
  const isAppRoute = pathname.startsWith("/app");
  const isDefiJouer = pathname.startsWith("/d/") || pathname.startsWith("/api/d/");
  const isChromeless = isAdminRoute || isAppRoute || isDefiJouer;
  // Accueil : le premier écran est Dr Lô en plein écran (pas d'en-tête ni de pied de page du site)
  // Écrans « Mon Dr Lô » (thème chaud sombre, plein écran) : pas d'en-tête ni de pied de page du site
  const isHome = pathname === "/" || pathname === "/compagnon" || pathname === "/espace" || pathname.startsWith("/espace/") || pathname === "/humeur" || pathname === "/journal" || pathname.startsWith("/journal/") || pathname === "/question" || pathname === "/drlo" || pathname === "/equipe" || pathname === "/confidentialite" || pathname === "/conditions" || pathname === "/cgu" || pathname === "/ethique" || pathname === "/faq" || pathname === "/contact" || pathname === "/patient/access" || pathname === "/patient" || pathname === "/messages-dr-lo" || pathname.startsWith("/messages-dr-lo/") || pathname === "/chaines" || pathname.startsWith("/chaines/") || pathname.startsWith("/c/") || pathname.startsWith("/pratique/") || pathname === "/routine" || pathname === "/boite-a-outils" || pathname === "/outils" || pathname === "/boite-a-questions" || pathname === "/assessment/totem" || pathname.startsWith("/assessment/results/") || pathname === "/defi" || pathname.startsWith("/defi/") || pathname === "/decouvre" || pathname === "/assessment/compatibility" || pathname.startsWith("/compat/") || pathname === "/assessment" || pathname.startsWith("/assessment/") || pathname === "/acheter-koris" || pathname === "/penc" || pathname.startsWith("/penc/") || pathname === "/mes-informations" || pathname === "/point-de-la-semaine" || pathname.startsWith("/p/") || pathname === "/espace-pro/rejoindre";
  const { currentUser } = useAuth();
  const userId = currentUser?.id ?? null;

  return (
    <>
      {!isChromeless && !isHome && <OptimizedHeader />}
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/compagnon" element={<CompagnonPage />} />
            <Route path="/espace" element={<EspacePage />} />
            <Route path="/mes-informations" element={<InfosPage />} />
            <Route path="/point-de-la-semaine" element={<PointPage />} />
            <Route path="/espace-pro/rejoindre" element={<ProApplyPage />} />
            <Route path="/penc" element={<PencPage />} />
            <Route path="/penc/:id" element={<PencPage />} />
            <Route path="/p/:id" element={<PencPage />} />
            <Route path="/espace/resultat/:scaleId" element={<ResultatPage />} />
            <Route path="/espace/:domaine" element={<DomainePage />} />
            <Route path="/humeur" element={<HumeurPage />} />
            <Route path="/question" element={<QuestionPage />} />
            {/* Espace de Dr Lô : séparé de l'admin, rôle staff/{uid}.drlo */}
            <Route path="/drlo" element={<DrLoConsole />} />
            <Route path="/equipe" element={<EquipePage />} />
            <Route path="/messages-dr-lo" element={<MessagesDrLoPage />} />
            <Route path="/messages-dr-lo/discussion/:qid" element={<MessagesDrLoPage />} />
            <Route path="/messages-dr-lo/:id" element={<MessagesDrLoPage />} />
            <Route path="/chaines" element={<ChainesPage />} />
            <Route path="/chaines/:id" element={<ChainesPage />} />
            <Route path="/c/:id" element={<ChannelInvitePage />} />
            <Route path="/pratique/:id" element={<PratiquePage />} />
            <Route path="/routine" element={<RoutinePage />} />
            <Route path="/boite-a-outils" element={<BoiteOutilsPage />} />
            <Route path="/outils" element={<OutilsPage />} />
            <Route path="/boite-a-questions" element={<BoitePage />} />

            {/* Companion app — layout mobile plein écran, distinct du site */}
            <Route
              path="/app"
              element={
                <ProtectedRoute userType="patient">
                  <CompanionApp />
                </ProtectedRoute>
              }
            />

            <Route path="/patient" element={<PatientAccess />} />
            <Route path="/patient/access" element={<PatientAccess />} />
            <Route
              path="/patient/forgot-password"
              element={<ForgotPassword />}
            />
            <Route
              path="/admin"
              element={<Navigate to="/admin/login" replace />}
            />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/login" element={<Navigate to="/" />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/confidentialite" element={<Privacy />} />
            <Route path="/conditions" element={<Terms />} />
            <Route path="/cgu" element={<Terms />} />
            <Route path="/ethique" element={<Ethics />} />
            <Route path="/verify-email" element={<VerifyEmail />} />

            {/* Patient Routes */}

            {/* Legacy redirects for old specialty URLs */}


            {/* Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute userType="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute userType="admin">
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/content"
              element={
                <ProtectedRoute userType="admin">
                  <AdminContent />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/notifications"
              element={
                <ProtectedRoute userType="admin">
                  <AdminNotifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/evaluations"
              element={
                <ProtectedRoute userType="admin">
                  <AdminEvaluations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/koris-credit"
              element={
                <ProtectedRoute userType="admin">
                  <AdminKorisCredit />
                </ProtectedRoute>
              }
            />


            {/* Journal Routes */}
            {/* Le journal vit désormais dans « Me confier » (une seule entité pour écrire et relire) */}
            <Route path="/journal" element={<MonJournalPage />} />
            <Route path="/journal/new" element={<Navigate to="/journal/ecrire" replace />} />
            <Route path="/journal/:id" element={<MonJournalPage />} />
            <Route path="/journal/:id/:edit" element={<MonJournalPage />} />

            {/* Mon Espace */}
            <Route path="/mon-espace" element={<Navigate to="/espace" replace />} />
            <Route path="/acheter-koris" element={<AcheterKorisPage />} />

            {/* Défi */}
            <Route path="/decouvre" element={<DecouvreDefiPage />} />
            <Route path="/defi" element={<DefiPage />} />
            <Route path="/defi/creer" element={<DefiCreerPage />} />
            <Route path="/defi/:slug/partage" element={<DefiPartagePage />} />
            <Route path="/defi/:slug/suivi" element={<DefiSuiviPage />} />
            <Route path="/defi/mes-defis" element={<DefiMesDefisPage />} />
            <Route path="/d/:slug" element={<DefiJouerPage />} />
            <Route path="/api/d/:slug" element={<DefiJouerPage />} />

            {/* Assessment Routes */}
            <Route path="/assessment" element={<Navigate to="/espace" replace />} />
            <Route path="/assessment/select" element={<Navigate to="/espace" replace />} />
            <Route path="/assessment/quiz/:sessionId" element={<AssessmentQuizPage />} />
            <Route path="/assessment/results/:sessionId" element={<AssessmentResultsPage />} />
            <Route path="/assessment/compatibility" element={<CompatibilityPage />} />
            <Route path="/compat/:code" element={<CompatibilityPage />} />
            <Route path="/assessment/test/:scaleId" element={<TestCodeRedirect />} />
            <Route path="/assessment/profile" element={<AssessmentProfilePage />} />
            <Route path="/assessment/totem" element={<TotemProfilePage />} />
            <Route path="/assessment/:category" element={<CategoryGate />} />

            {/* Fallback route - évite les 404 "profonds" */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      {!isChromeless && !isHome && <Footer />}
      {!isChromeless && <FloatingChat userId={userId} />}
      <CompatInviteResume />
      {!isChromeless && <BottomTabs />}
      {!isChromeless && !isHome && !!currentUser && <FloatingKori />}
      {!isChromeless && <NoKorisModal />}
      {!isChromeless && <KorisWelcome />}
      {!isChromeless && <WelcomeFlow />}
    </>
  );
};

function App() {
  // Track if component is mounted
  const isMountedRef = useRef(true);
  const resetAttemptedRef = useRef(false);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up all Firestore listeners when the app unmounts
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      cleanupAllProfessionalsListeners();
      cleanupAllBookingListeners();
      cleanupAllMessageListeners();
      clearMessageCaches();
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = null;
      }
    };
  }, []);

  // CRITICAL: Handle page visibility changes to manage listeners
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "hidden") {
        // App going to background, cleaning up listeners
        cleanupAllProfessionalsListeners();
        cleanupAllBookingListeners();
        cleanupAllMessageListeners();
        clearMessageCaches();
      } else if (document.visibilityState === "visible") {
        // Ensure Firestore is ready when app becomes visible again
        try {
          await ensureFirestoreReady();
        } catch {
          // Failed to ensure Firestore ready on visibility change
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // CRITICAL: Ensure Firestore is ready when the app mounts
  useEffect(() => {
    const initializeFirestore = async () => {
      try {
        await ensureFirestoreReady();

        // Only ensure required collections exist if Firestore is actually ready
        try {
          // Add a small delay to ensure Firestore is fully initialized
          await new Promise((resolve) => setTimeout(resolve, 100));
          await ensureRequiredCollectionsExist();
        } catch {
          // Failed to ensure required collections exist
          // Don't throw here - the app can still function without this check
        }
      } catch {
        // Failed to ensure Firestore ready on app mount
      }
    };

    initializeFirestore();
  }, []);

  // CRITICAL: Handle Firestore internal errors globally
  useEffect(() => {
    const handleError = async (event: ErrorEvent) => {
      // Check if this is a Firestore internal error
      if (
        event.error &&
        typeof event.error.message === "string" &&
        (event.error.message.includes("INTERNAL ASSERTION FAILED") ||
          event.error.message.includes("Target ID already exists") ||
          event.error.message.includes("client has already been terminated") ||
          event.error.message.includes("Unexpected state"))
      ) {
        // Prevent multiple reset attempts in quick succession
        if (resetAttemptedRef.current) {
          return;
        }

        resetAttemptedRef.current = true;

        // Clear any existing timeout
        if (resetTimeoutRef.current) {
          clearTimeout(resetTimeoutRef.current);
        }

        // Set a timeout to allow reset attempts again after 10 seconds
        resetTimeoutRef.current = setTimeout(() => {
          resetAttemptedRef.current = false;
        }, 10000);

        // Clean up all listeners first
        cleanupAllProfessionalsListeners();
        cleanupAllBookingListeners();
        cleanupAllMessageListeners();
        clearMessageCaches();

        // Try to reset Firestore connection - this already handles IndexedDB cleanup
        try {
          await resetFirestoreConnection();

          // Ensure Firestore is ready after reset
          await ensureFirestoreReady();
        } catch {
          // Failed to reset Firestore connection after global error
        }
      }
    };

    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener("error", handleError);
    };
  }, []);

  // Auto-reload si les chunks lazy échouent (nouveau build déployé)
  useEffect(() => {
    const handler = (e: ErrorEvent | PromiseRejectionEvent) => {
      const msg = String(
        (e as ErrorEvent)?.message ||
          (e as PromiseRejectionEvent)?.reason?.message ||
          (e as PromiseRejectionEvent)?.reason ||
          ""
      );

      // Vite/webpack: quand un nouvel artefact est déployé, l'ancien onglet
      // n'arrive plus à charger les chunks -> on recharge proprement.
      // TEMPORAIREMENT COMMENTÉ POUR ÉVITER LES BOUCLES DE RELOAD
      // if (
      //   /ChunkLoadError|Loading chunk \d+ failed|CSS_CHUNK_LOAD_FAILED/i.test(
      //     msg
      //   )
      // ) {
      //   console.warn("🆕 Nouveau build détecté. Recharge de la page…");
      //   window.location.reload();
      // }
    };

    window.addEventListener("error", handler);
    window.addEventListener("unhandledrejection", handler);
    return () => {
      window.removeEventListener("error", handler);
      window.removeEventListener("unhandledrejection", handler);
    };
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <KorisProvider>
          <LanguageProvider>
            <TermsProvider>
              <ScrollToTop />
              <UpdateBanner />
              {/* Mettre ceci UNE SEULE FOIS dans App() (en dehors des pages) */}
              <div
                id="recaptcha-container"
                style={{
                  position: "fixed",
                  left: -9999,
                  top: -9999,
                  width: 1,
                  height: 1,
                }}
              />
              <div className="min-h-screen flex flex-col" style={{ background: "#17120E" }}>
                <AppChrome />
              </div>
            </TermsProvider>
          </LanguageProvider>
          </KorisProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
